/**
 * Map hero.
 *
 * A live map poster sitting in the homepage hero. It is not a picture of the
 * product — it is the product, rendered from the same palettes file and the
 * same PMTiles archive the editor uses, so the palette chips can recolour it in
 * place and the visitor sees what they are buying before they open the editor.
 *
 * Deliberately separate from map-poster-editor.js. The editor is a form with a
 * map in it; this is a map with a caption. They share the palette data and the
 * tile archive, so colour and content cannot drift apart — only the layer
 * recipe is written twice, and this one is free to be simpler.
 *
 * The map is non-interactive on purpose. A hero that swallows a drag or a
 * scroll is a worse hero, and the palette chips already carry the interaction.
 */

const HERO_MAPLIBRE_CSS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css';
const HERO_MAPLIBRE_JS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js';
const HERO_PMTILES_JS = 'https://unpkg.com/pmtiles@3.2.1/dist/pmtiles.js';

// Protomaps v4 road kinds, widest first. Same spread as the editor so the two
// read as the same product.
const HERO_ROAD_KINDS = [
  { kinds: ['highway'], key: 'motorway', width: 2.2 },
  { kinds: ['major_road'], key: 'primary', width: 1.5 },
  { kinds: ['medium_road'], key: 'secondary', width: 0.95 },
  { kinds: ['minor_road'], key: 'minor', width: 0.45 },
  { kinds: ['path'], key: 'path', width: 0.3 },
];

// A fill handed a LineString triangulates it as a ring and paints wedges across
// the map. The water layer carries river centrelines as well as polygons.
const HERO_POLYGONS_ONLY = ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]];

const HERO_PIN_SVG = `<svg viewBox="0 0 24 32" width="26" height="34" aria-hidden="true">
  <path d="M12 0C5.37 0 0 5.37 0 12c0 8.5 12 20 12 20s12-11.5 12-20C24 5.37 18.63 0 12 0z" fill="#c0392b"/>
  <circle cx="12" cy="12" r="4.3" fill="#ffffff"/>
</svg>`;

/**
 * Data-driven fill for a confetti palette: colour each land use polygon from
 * the palette's `confetti` list by a hash of its feature id. Mirrors the
 * editor, so a palette looks the same in the hero and the poster.
 */
function heroConfettiFill(pal) {
  const colors = pal.confetti;
  if (!Array.isArray(colors) || !colors.length) return null;
  const match = ['match', ['%', ['coalesce', ['id'], 0], colors.length]];
  colors.forEach((c, i) => match.push(i, c));
  match.push(colors[colors.length - 1]);
  return match;
}

/** Tileable confetti swatch for the ground. Same recipe as the editor. */
function heroConfettiImage(colors, size = 256, cell = 16) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  let seed = 0x9e3779b9;
  const rnd = () => {
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    return (seed >>> 0) / 4294967296;
  };
  const n = Math.round(size / cell);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      ctx.fillStyle = colors[Math.floor(rnd() * colors.length)];
      ctx.fillRect(x * cell, y * cell, cell, cell);
    }
  }
  return ctx.getImageData(0, 0, size, size);
}

/** Load a stylesheet once. */
function heroCss(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const el = document.createElement('link');
  el.rel = 'stylesheet';
  el.href = href;
  document.head.appendChild(el);
}

/** Load an external script once. */
function heroScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const el = document.createElement('script');
    el.src = src;
    el.onload = resolve;
    el.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(el);
  });
}

class MapHero extends HTMLElement {
  connectedCallback() {
    this.mapEl = this.querySelector('[data-map]');
    this.chipGrid = this.querySelector('[data-palettes-grid]');
    this.view = {
      lat: parseFloat(this.dataset.lat) || 19.076,
      lon: parseFloat(this.dataset.lon) || 72.8777,
      zoom: parseFloat(this.dataset.zoom) || 11.4,
    };
    this.tileUrl = this.dataset.tileUrl;
    this.wantPalette = this.dataset.palette || 'terracotta';
    this.bounds = this.parseBounds(this.dataset.bounds);
    this.started = false;

    this.loadPalettes().then(() => {
      this.buildChips();
      this.watchForView();
    });
  }

  /* ---------------------------------------------------------------- data */

  async loadPalettes() {
    try {
      this.palettes = await fetch(this.dataset.palettes).then((r) => r.json());
    } catch (err) {
      this.palettes = [];
    }
    this.palette =
      this.palettes.find((p) => p.id === this.wantPalette) || this.palettes[0];
  }

  /**
   * Nothing is fetched until the hero is actually on screen. The homepage has
   * enough to load without pulling a map library for a section the visitor may
   * never scroll to.
   */
  watchForView() {
    if (!this.mapEl || !this.palette) return;
    if (!('IntersectionObserver' in window)) return this.start();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.start();
      },
      { rootMargin: '200px' }
    );
    this.observer.observe(this);
  }

  async start() {
    if (this.started) return;
    this.started = true;
    try {
      heroCss(HERO_MAPLIBRE_CSS);
      await heroScript(HERO_MAPLIBRE_JS);
      await heroScript(HERO_PMTILES_JS);
      this.initMap();
    } catch (err) {
      // The poster keeps its flat palette ground, which still looks deliberate.
      this.setAttribute('data-map-failed', '');
      console.warn('[map-hero] could not load the map library', err);
    }
  }

  /* --------------------------------------------------------------- style */

  /** Same layer recipe as the editor, minus buildings. */
  buildStyle(pal) {
    const conf = heroConfettiFill(pal);

    const layers = [
      { id: 'background', type: 'background', paint: { 'background-color': pal.bg } },
      {
        id: 'water',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'water',
        filter: HERO_POLYGONS_ONLY,
        paint: { 'fill-color': pal.water },
      },
      {
        id: 'landcover',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'landcover',
        filter: HERO_POLYGONS_ONLY,
        paint: { 'fill-color': pal.park, 'fill-opacity': 0.7 },
      },
      {
        id: 'landuse',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'landuse',
        filter: HERO_POLYGONS_ONLY,
        paint: { 'fill-color': conf || pal.park, 'fill-opacity': conf ? 1 : 0.85 },
      },
    ];

    HERO_ROAD_KINDS.forEach((r) => {
      layers.push({
        id: `road-casing-${r.key}`,
        type: 'line',
        source: 'protomaps',
        'source-layer': 'roads',
        filter: ['in', ['get', 'kind'], ['literal', r.kinds]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': pal.ink,
          'line-opacity': 0.12,
          'line-width': [
            'interpolate',
            ['exponential', 1.6],
            ['zoom'],
            6,
            0.4 * pal.road_scale,
            16,
            r.width * 6 * pal.road_scale,
          ],
        },
      });
    });
    HERO_ROAD_KINDS.forEach((r) => {
      layers.push({
        id: `road-${r.key}`,
        type: 'line',
        source: 'protomaps',
        'source-layer': 'roads',
        filter: ['in', ['get', 'kind'], ['literal', r.kinds]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': pal.roads[r.key],
          'line-width': [
            'interpolate',
            ['exponential', 1.6],
            ['zoom'],
            6,
            0.25 * pal.road_scale,
            16,
            r.width * 4 * pal.road_scale,
          ],
        },
      });
    });

    return {
      version: 8,
      sources: {
        protomaps: {
          type: 'vector',
          url: `pmtiles://${this.tileUrl}`,
          attribution: '© OpenStreetMap contributors',
        },
      },
      layers,
    };
  }

  /* ----------------------------------------------------------------- map */

  /** "minLon,minLat,maxLon,maxLat" to a MapLibre bounds, or null. */
  parseBounds(raw) {
    const n = String(raw || '').split(',').map(Number);
    if (n.length !== 4 || !n.every(Number.isFinite)) return null;
    return [
      [n[0], n[1]],
      [n[2], n[3]],
    ];
  }

  /**
   * Hold the view on the configured box. A fixed zoom cannot keep a whole city
   * in the poster, because the poster's height is derived from the viewport —
   * the same zoom frames a different amount of ground on a laptop and a phone.
   * Fitting a box is what actually guarantees the city fits.
   */
  frameView() {
    if (!this.map) return;
    if (this.bounds) {
      this.map.fitBounds(this.bounds, { animate: false });
    } else {
      this.map.jumpTo({ center: [this.view.lon, this.view.lat], zoom: this.view.zoom });
    }
  }

  initMap() {
    const protocol = new pmtiles.Protocol();
    maplibregl.addProtocol('pmtiles', protocol.tile);

    this.map = new maplibregl.Map({
      container: this.mapEl,
      style: this.buildStyle(this.palette),
      center: [this.view.lon, this.view.lat],
      zoom: this.view.zoom,
      interactive: false,
      attributionControl: false,
      preserveDrawingBuffer: true,
    });

    // Re-frame as well as resize: a fitted box has to be re-fitted when the
    // poster changes shape, or the city drifts off centre.
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.map) return;
        this.map.resize();
        this.frameView();
      });
      this.resizeObserver.observe(this.mapEl);
    }

    this.frameView();

    this.map.on('style.load', () => this.applyConfettiPattern());

    this.map.on('load', () => {
      this.frameView();
      this.dropPin();
      this.removeAttribute('data-loading');
    });
    this.map.on('error', (e) => console.warn('[map-hero] map error', e && e.error));
  }

  applyConfettiPattern() {
    const colors = this.palette && this.palette.confetti;
    if (!this.map || !this.map.getLayer('background')) return;
    if (!Array.isArray(colors) || !colors.length) return;
    const id = 'confetti-ground';
    if (this.map.hasImage(id)) this.map.removeImage(id);
    this.map.addImage(id, heroConfettiImage(colors), { pixelRatio: 1 });
    this.map.setPaintProperty('background', 'background-pattern', id);
  }

  dropPin() {
    if (!this.map || this.pin) return;
    const el = document.createElement('div');
    el.className = 'map-hero__pin';
    // The drop animation lives on an inner element. MapLibre positions a marker
    // with an inline `transform`, and a CSS animation of `transform` on the same
    // element wins over it — the pin ends up pinned to the map's origin.
    el.innerHTML = `<span class="map-hero__pin-inner">${HERO_PIN_SVG}</span>`;
    this.pin = new maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([this.view.lon, this.view.lat])
      .addTo(this.map);
  }

  /* ------------------------------------------------------------- palette */

  buildChips() {
    if (!this.chipGrid || !this.palettes.length) return;
    this.chipGrid.innerHTML = '';
    this.palettes.forEach((p) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'map-hero__chip';
      btn.dataset.palette = p.id;
      btn.setAttribute('aria-label', `${p.label} palette`);
      btn.innerHTML = `<span class="map-hero__chip-dot" style="--c1:${p.swatch[0]};--c2:${p.swatch[2]}"></span><span class="map-hero__chip-name">${p.label}</span>`;
      btn.addEventListener('click', () => this.choose(p));
      this.chipGrid.appendChild(btn);
    });
    this.syncChips();
  }

  choose(p) {
    this.palette = p;
    this.syncChips();
    if (this.map) this.map.setStyle(this.buildStyle(p));
  }

  syncChips() {
    this.querySelectorAll('[data-palette]').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.palette === this.palette?.id);
    });
  }
}

if (!customElements.get('map-hero')) {
  customElements.define('map-hero', MapHero);
}
